import { useEffect, useState } from 'react';
import { LAUNCH_DEADLINE, LAUNCH_WINDOW_HOURS } from '../config/launch';

type CountdownState = {
  hours: string;
  minutes: string;
  seconds: string;
  expired: boolean;
};

const STORAGE_KEY = 'screen-adapt-launch-deadline';
const WINDOW_MS = LAUNCH_WINDOW_HOURS * 60 * 60 * 1000;

const pad = (value: number): string => String(Math.max(0, value)).padStart(2, '0');

function getDeadline(): number {
  const hardDeadline = new Date(LAUNCH_DEADLINE).getTime();

  if (typeof window === 'undefined') {
    return Date.now() + WINDOW_MS;
  }

  let rolling = Number(window.localStorage.getItem(STORAGE_KEY));
  if (!Number.isFinite(rolling) || rolling <= Date.now()) {
    rolling = Date.now() + WINDOW_MS;
    try {
      window.localStorage.setItem(STORAGE_KEY, String(rolling));
    } catch {
      /* storage unavailable — the rolling window just restarts on reload */
    }
  }

  return Number.isFinite(hardDeadline) ? Math.min(rolling, hardDeadline) : rolling;
}

function compute(deadline: number): CountdownState {
  const remaining = deadline - Date.now();
  if (!Number.isFinite(deadline) || remaining <= 0) {
    return { hours: '00', minutes: '00', seconds: '00', expired: true };
  }
  const totalSeconds = Math.floor(remaining / 1000);
  return {
    hours: pad(Math.floor(totalSeconds / 3600)),
    minutes: pad(Math.floor((totalSeconds % 3600) / 60)),
    seconds: pad(totalSeconds % 60),
    expired: false,
  };
}

export function useCountdown(): CountdownState {
  const [deadline] = useState<number>(() => getDeadline());
  const [state, setState] = useState<CountdownState>(() => compute(deadline));

  useEffect(() => {
    const id = window.setInterval(() => setState(compute(deadline)), 1000);
    return () => window.clearInterval(id);
  }, [deadline]);

  return state;
}
